import React from 'react';
import DialogTitle from '../../DialogTitle';
import { IonicButton, IonicInput } from 'ionic-ui';
import { useFormContext, Controller } from 'react-hook-form';
import z from 'zod';
import { newProductSchema } from '../../../../schemas/newProductSchema';
import clsx from 'clsx';
import { AutoCompleteInput } from '../../../ui/AutoCompleteInput';
import { AsyncMultiAutoComplete } from '../../../ui/AsyncMultiAutoComplete/AsyncMultiAutoComplete';
import CustomToggle from '../../settings/ui/Toggles';
import useProductCategory from '../../../../services/Products/hooks/useProductCategory';
import { formatPrice } from '../../../../utils/formatPrice';
import useProductHashTag from '../../../../services/Products/hooks/useProductHashTag';

export default function StepOne({
  onNext,
  onSaveToDraft,
}: {
  onNext?: () => void;
  onSaveToDraft?: () => void;
}) {
  const { control, watch } = useFormContext<z.infer<typeof newProductSchema>>();
  const { addNewHashtag, getAvailableHashTags } = useProductHashTag();
  //FUNCTION FOR GETTING HASHTAG OPTIONS
  const getAvailableCategories = useProductCategory();

  const errorStyle = clsx('text-red-600');
  return (
    <div className="space-y-4 sm:w-[55vw] max-xlg:w-[660px] dialog-content">
      <DialogTitle>Add New Product</DialogTitle>
      <p className="medium-emphasis-text">
        Please add a new product to your catalog
      </p>

      {/* FORM */}
      <div className="w-full space-y-6">
        {/* PRODUCT NAME */}
        <Controller
          name="name"
          control={control}
          render={({ field, fieldState }) => (
            <IonicInput
              {...field}
              label="Product Name *"
              placeholder="e.g. Paracetamol 500mg"
              hint={fieldState.error?.message}
              hintClass={errorStyle}
            />
          )}
        />

        {/* CATEGORY */}
        <Controller
          name="category"
          control={control}
          render={({ field, fieldState }) => (
            <AutoCompleteInput
              disabled={field.disabled}
              value={field.value}
              fetchOptions={getAvailableCategories}
              onChange={(value: any) => field.onChange(value)}
              label="Category*"
              hintMsg={fieldState.error?.message}
              className="w-full" // Tailwind for layout
            />
          )}
        />
        {/* HASH TAGS */}
        <Controller
          name="hash_tag"
          control={control}
          render={({ field, fieldState }) => (
            <AsyncMultiAutoComplete
              fetchOptions={getAvailableHashTags}
              addFn={addNewHashtag}
              getOptionLabel={(u) => u.name}
              value={field.value}
              onChange={field.onChange}
              label="hash tag*"
              hintMsg={fieldState.error?.message}
              placeholder="Please enter product hashtag (e.g drug)"
            />
          )}
        />

        {/* PRICE */}
        <Controller
          name="price"
          control={control}
          render={({ field, fieldState }) => (
            <IonicInput
              {...field}
              prefix="N"
              type="number"
              containerClass="quantity-counter"
              label="Price *"
              placeholder="0.00"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                field.onChange(Number(event.target.value))
              }
              hint={fieldState.error?.message}
              hintClass={errorStyle}
            />
          )}
        />

        <div className="space-y-2">
          <Controller
            name="isDiscounted"
            control={control}
            render={({ field }) => (
              <CustomToggle
                name={field.name}
                disabled={field.disabled}
                checked={field.value}
                label="Add discount"
                subLabel="Make product price discounted for a short period"
                onToggle={(checked) => {
                  field.onChange(checked.valueOf());
                }}
              />
            )}
          />
          {/* DISCOUNT */}
          <div
            className={`space-y-4 border border-border p-4 rounded-md bg-gray-50  ${watch('isDiscounted') ? 'block' : 'hidden'}`}
          >
            {/* DISCOUNT PERCENTAGE */}
            <Controller
              name="discount_amount"
              control={control}
              render={({ field, fieldState }) => (
                <div className="space-y-1">
                  <IonicInput
                    {...field}
                    prefix="%"
                    type="number"
                    containerClass="quantity-counter"
                    label="Discount (if available)"
                    placeholder="e.g 10 for 10%"
                    max={100}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const raw = event.target.value;
                      if (!raw) return field.onChange('');
                      const numValue = Number(raw);
                      if (numValue < 100) {
                        field.onChange(numValue); // pass the number to react-hook-form
                      } else field.onChange(100);
                    }}
                    hint={fieldState.error?.message}
                    hintClass={errorStyle}
                  />
                  <small className="flex items-center gap-1 high-emphasis-text">
                    <span className="medium-emphasis-text">
                      Discounted Price:
                    </span>
                    <span className="font-medium">
                      {formatPrice(
                        (watch('price') *
                          (100 - (watch('discount_amount') || 0))) /
                          100 || 0
                      )}
                    </span>
                  </small>
                </div>
              )}
            />
            {/* <div className="flex justify-between items-start medium-emphasis-text"> */}
            {/* STARTING DATE FOR DISCOUNT  */}
            {/* <Controller
                name="discount_starts_at"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <DatePickerButton
                    id="start-date" // must match IonDatetimeButton datetime prop
                    value={field.value || new Date().toISOString().slice(0, 19)} // pass the current value
                    onChange={field.onChange}
                    label="Discount starts"
                    min={new Date().toISOString().slice(0, 19)}
                  />
                )}
              /> */}

            {/* ENDING DATE FOR DISCOUNT */}
            {/* <Controller
                name="discount_ends_at"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <DatePickerButton
                    id="end-date" // must match IonDatetimeButton datetime prop
                    value={
                      field.value ||
                      watch('discount_starts_at') ||
                      new Date().toISOString().slice(0, 19)
                    } // pass the current value
                    onChange={field.onChange}
                    label="discount ends"
                    min={
                      watch('discount_starts_at') ||
                      new Date().toISOString().slice(0, 19)
                    } //ENDING DATE CANNOT BE EARLIER THAN STARTING DATE
                  />
                )} 
              />*/}
            {/* </div> */}
          </div>
        </div>

        {/* STOCK QUANTITY */}
        <Controller
          name="quantity"
          control={control}
          render={({ field, fieldState }) => (
            <IonicInput
              {...field}
              type="number"
              containerClass="quantity-counter"
              label="Stock Quantity *"
              placeholder="0"
              min={0}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                field.onChange(Number(event.target.value))
              }
              hint={fieldState.error?.message}
              hintClass={errorStyle}
            />
          )}
        />

        {/* DESCRIPTION */}
        <Controller
          name="description"
          control={control}
          render={({ field, fieldState }) => (
            <IonicInput
              {...field}
              type="textarea"
              className="resize-none"
              label="Product Description *"
              placeholder="Product description, usage, dosage, e.t.c"
              hint={fieldState.error?.message}
              hintClass={errorStyle}
            />
          )}
        />
      </div>

      <div className="w-full flex flex-wrap gap-2">
        <IonicButton
          variant="outline"
          type="reset"
          className="rounded-sm min-w-fit max-sm:flex-1 sm:w-24"
        >
          Cancel
        </IonicButton>

        {onSaveToDraft && (
          <IonicButton
            variant="outline"
            type="button"
            className="rounded-sm min-w-fit max-sm:flex-1 sm:w-24"
            onClick={onSaveToDraft}
          >
            Save Draft
          </IonicButton>
        )}

        <IonicButton
          className="rounded-sm w-auto max-sm:flex-1 sm:ml-auto min-w-fit sm:w-24"
          type="button"
          onClick={onNext}
        >
          Next
        </IonicButton>
      </div>
    </div>
  );
}
